// driftPresetInterpolation
// Utility for interpolating drift parameters based on a single intensity slider (0–100).

export const VISUALIZER_ANCHORS = [
    { t: 0, amplitudeX: 0, amplitudeY: 0, rotation: 0, speed: 0, scale: 0, acceleration: 0 },
    { t: 33, amplitudeX: 5, amplitudeY: 5, rotation: 0.5, speed: 1.0, scale: 0.3, acceleration: 50 },
    { t: 66, amplitudeX: 15, amplitudeY: 15, rotation: 1.5, speed: 0.35, scale: 0.5, acceleration: 25 },
    { t: 100, amplitudeX: 25, amplitudeY: 25, rotation: 2.5, speed: 0.5, scale: 0.75, acceleration: 10 },
];

export const BACKGROUND_ANCHORS = [
    { t: 0, amplitudeX: 0, amplitudeY: 0, rotation: 0, speed: 0, scale: 0, acceleration: 0 },
    { t: 33, amplitudeX: 5, amplitudeY: 5, rotation: 0.5, speed: 1.5, scale: 0.5, acceleration: 5 },
    { t: 66, amplitudeX: 25, amplitudeY: 25, rotation: 5.0, speed: 0.4, scale: 0, acceleration: 30 },
    { t: 100, amplitudeX: 50, amplitudeY: 50, rotation: 10.0, speed: 0.5, scale: 0, acceleration: 50 },
];

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function areClose(a, b, tolerance) {
    return Math.abs(a - b) <= tolerance;
}

function interpolateAnchors(anchors, intensity) {
    if (intensity <= 0) return { ...anchors[0] };
    if (intensity >= 100) return { ...anchors[anchors.length - 1] };

    // Find the two adjacent anchors
    let lower = anchors[0];
    let upper = anchors[anchors.length - 1];

    for (let i = 0; i < anchors.length - 1; i++) {
        if (intensity >= anchors[i].t && intensity <= anchors[i + 1].t) {
            lower = anchors[i];
            upper = anchors[i + 1];
            break;
        }
    }

    const segmentT = (intensity - lower.t) / (upper.t - lower.t);

    return {
        amplitudeX: Math.round(lerp(lower.amplitudeX, upper.amplitudeX, segmentT)),
        amplitudeY: Math.round(lerp(lower.amplitudeY, upper.amplitudeY, segmentT)),
        rotation: +lerp(lower.rotation, upper.rotation, segmentT).toFixed(1),
        speed: +lerp(lower.speed, upper.speed, segmentT).toFixed(2),
        scale: +lerp(lower.scale ?? 0, upper.scale ?? 0, segmentT).toFixed(2),
        acceleration: Math.round(lerp(lower.acceleration ?? 0, upper.acceleration ?? 0, segmentT)),
        enabled: intensity > 0,
    };
}

export function interpolateVisualizerDrift(intensity) {
    return interpolateAnchors(VISUALIZER_ANCHORS, intensity);
}

export function interpolateBackgroundDrift(intensity) {
    return interpolateAnchors(BACKGROUND_ANCHORS, intensity);
}

/**
 * Determines the approximate intensity (0-100) from current drift parameters.
 * Returns null for custom drift combinations.
 *
 * Important: we only return an intensity when all interpolated fields match the
 * preset curve. This prevents "partial" matches (for example, only amplitudeX)
 * from forcing the Intensity slider to move while users edit Customize controls.
 */
export function estimateIntensity(driftConfig, anchors) {
    // Use amplitudeX as the primary metric
    if (!driftConfig || !driftConfig.enabled) return 0;

    const ax = driftConfig.amplitudeX ?? 0;

    for (let i = 0; i < anchors.length - 1; i++) {
        const lower = anchors[i];
        const upper = anchors[i + 1];
        if (ax >= lower.amplitudeX && ax <= upper.amplitudeX) {
            let intensity = lower.t;

            if (upper.amplitudeX !== lower.amplitudeX) {
                const t = (ax - lower.amplitudeX) / (upper.amplitudeX - lower.amplitudeX);
                intensity = Math.round(lerp(lower.t, upper.t, t));
            }

            const estimatedDrift = interpolateAnchors(anchors, intensity);
            // Strict equality (with tiny floating tolerance) keeps the slider in
            // "custom" mode as soon as any individual drift field diverges.
            const matchesPreset =
                areClose(driftConfig.amplitudeX ?? 0, estimatedDrift.amplitudeX ?? 0, 0.0001) &&
                areClose(driftConfig.amplitudeY ?? 0, estimatedDrift.amplitudeY ?? 0, 0.0001) &&
                areClose(driftConfig.rotation ?? 0, estimatedDrift.rotation ?? 0, 0.0001) &&
                areClose(driftConfig.speed ?? 0, estimatedDrift.speed ?? 0, 0.0001) &&
                areClose(driftConfig.scale ?? 0, estimatedDrift.scale ?? 0, 0.0001) &&
                areClose(driftConfig.acceleration ?? 0, estimatedDrift.acceleration ?? 0, 0.0001);

            return matchesPreset ? intensity : null;
        }
    }
    return null; // Custom — doesn't fit the curve
}

/**
 * Returns the effective drift config that preview components should use.
 * When customMode is true, the stored custom values are used directly.
 * When customMode is false, drift params are computed from the stored intensity.
 * @param {object} drift - The raw drift config from store
 * @param {Array}  anchors - The anchor array (VISUALIZER_ANCHORS or BACKGROUND_ANCHORS)
 * @returns {object} Resolved drift config for the renderer
 */
export function getEffectiveDrift(drift, anchors) {
    if (!drift || !drift.enabled) return drift;
    if (drift.customMode) return drift;

    const intensity = drift.intensity ?? 0;

    if (intensity === 0)
        return { ...drift, enabled: false };

    const preset = interpolateAnchors(anchors, intensity);

    return {
        ...drift,
        ...preset,
        enabled: drift.enabled,
    };
}
